import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import TambaLogo from "@/components/TambaLogo";
import { Phone, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [debugOtp, setDebugOtp] = useState("");

  const handleSendOTP = async () => {
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone });
      if (response.data.success) {
        setDebugOtp(response.data.debug_otp);
        setStep("otp");
        toast.success("OTP sent successfully!");
      }
    } catch (error) {
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter complete OTP");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      if (response.data.success) {
        // Store user data
        localStorage.setItem("tamba_user", JSON.stringify(response.data.user));
        
        if (response.data.is_new) {
          toast.success("Welcome to Tamba! Let's set up your profile.");
          navigate("/onboarding");
        } else if (!response.data.user.onboarding_completed) {
          toast.success("Welcome back! Let's complete your profile setup.");
          navigate("/onboarding");
        } else {
          toast.success("Welcome back!");
          navigate("/dashboard");
        }
      }
    } catch (error) {
      toast.error("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative overflow-hidden">
      {/* Decorative backgrounds */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-tamba-blue/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-tamba-yellow/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-tamba-teal/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      
      <div className="absolute top-6 left-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/")}
          className="gap-2"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative"
      >
        <Card className="border-border/50 shadow-2xl bg-card/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", bounce: 0.5 }}
              >
                <TambaLogo className="h-20 w-20" />
              </motion.div>
            </div>
            <CardTitle className="font-heading text-2xl">
              {step === "phone" ? "Welcome!" : "Enter Verification Code"}
            </CardTitle>
            <CardDescription>
              {step === "phone" 
                ? "Enter your phone number to get started" 
                : `We sent a code to ${phone}`
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            {step === "phone" ? (
              <div className="space-y-4">
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-12 h-14 text-lg rounded-xl border-border focus:border-tamba-blue focus:ring-tamba-blue"
                    data-testid="phone-input"
                  />
                </div>
                <Button 
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full h-14 rounded-full text-lg bg-tamba-blue hover:bg-tamba-blue/90 tamba-shadow"
                  data-testid="send-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    "Continue"
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(value) => setOtp(value)}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-12 h-14 text-xl border-border focus:border-tamba-blue" />
                      <InputOTPSlot index={1} className="w-12 h-14 text-xl border-border focus:border-tamba-blue" />
                      <InputOTPSlot index={2} className="w-12 h-14 text-xl border-border focus:border-tamba-blue" />
                      <InputOTPSlot index={3} className="w-12 h-14 text-xl border-border focus:border-tamba-blue" />
                      <InputOTPSlot index={4} className="w-12 h-14 text-xl border-border focus:border-tamba-blue" />
                      <InputOTPSlot index={5} className="w-12 h-14 text-xl border-border focus:border-tamba-blue" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                {debugOtp && (
                  <div className="bg-tamba-teal/10 border border-tamba-teal/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Demo OTP (auto-filled in test)</p>
                    <p className="font-mono text-lg font-bold text-tamba-teal">{debugOtp}</p>
                  </div>
                )}
                
                <Button 
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                  className="w-full h-14 rounded-full text-lg bg-tamba-blue hover:bg-tamba-blue/90 tamba-shadow"
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    "Verify & Continue"
                  )}
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => setStep("phone")}
                  className="w-full text-tamba-blue hover:text-tamba-blue/80"
                  data-testid="change-number-btn"
                >
                  Change phone number
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AuthPage;
